// src/context/AuthContext.jsx
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { clearSession, isTokenExpired } from "../services/sessionUtils";

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);

  // Lee credenciales de localStorage y decide si hay sesión válida
  const readSession = () => {
    const email = localStorage.getItem("email") || "";
    const rol = localStorage.getItem("rol") || "";
    const token = localStorage.getItem("token") || "";

    if (email && rol && token && !isTokenExpired(token)) return { email, rol, token };
    if (token) clearSession();
    return null;
  };

  // Carga inicial
  useEffect(() => {
    setUser(readSession());
  }, []);

  // Reacciona a cambios en localStorage (otras pestañas o flujos de login/logout)
  useEffect(() => {
    const onStorage = (ev) => {
      if (ev.key === "email" || ev.key === "rol" || ev.key === "token") {
        setUser(readSession());
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // --- Helpers para usar desde pantallas de login/logout ---

  // Guarda credenciales y notifica a oyentes (PermisosContext, etc.)
  const login = ({ email, rol, token }) => {
    if (email) localStorage.setItem("email", email);
    if (rol) localStorage.setItem("rol", rol);
    if (token) localStorage.setItem("token", token);

    const session = readSession();
    setUser(session);

    // Notifica a contexts que escuchan "storage" y a PermisosContext
    window.dispatchEvent(new StorageEvent("storage", { key: "token" }));
    window.dispatchEvent(new Event("auth-changed")); // 👈 añadido
  };

  // Limpia credenciales y notifica
  const logout = () => {
    clearSession();

    setUser(null);

    // Notifica a contexts que escuchan "storage" y a PermisosContext
    window.dispatchEvent(new StorageEvent("storage", { key: "token" }));
    window.dispatchEvent(new Event("auth-changed")); // 👈 añadido
  };

  const isAuthenticated = useMemo(() => !!user?.token, [user]);

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, isAuthenticated }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
